import { Card, CardContent, CardHeader } from '@/app/common/components/Card.tsx'
import { Button } from '@/app/common/components/button/Button.tsx'
import Label from '@/app/common/components/Label'
import AutoDistributionCard from '../shared/AutoDistributionCard'
import DistributionRulesCard from '../shared/DistributionRulesCard'
import { School } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ConfirmChangeRuleDialog } from '../shared/ChangeRuleDialog'
import { useState } from 'react'
import { PartialPurseData } from '@/pages/program_purse_settings/purse-details/_components/distribution-settings-options/types.ts'
import {
  ModifierCode,
  ProgramPurseResource,
} from '@/queries/program_purses/schemas.ts'
import { useForm } from 'react-hook-form'
import { useUpdateEducationDistributions } from '@/queries/purse_configs/useUpdateEducationDistributions.ts'
import { useQueryClient } from '@tanstack/react-query'
import { PURSE_KEYS } from '@/queries/program_purses/keys.ts'
import { useToast } from '@/shad/ui/toast/use-toast.ts'
import ControlledCurrencyInput from '../shared/ControlledCurrencyInput'
import { SubmitButton } from '@/app/common/components/SubmitButton.tsx'
import { useUpdateProgramPurse } from '@/queries/purse_configs/useUpdateProgramPurse.ts'
import SpecialNeedsAwardCard from '../shared/SpecialNeedsAwardCard'
import { ExtraModifierData } from '../modifiers/types'
import { ExtraModifierCards } from '../shared/ExtraModifierCard'

type UIVariant = 'form_view' | 'all_settings_view'

type Props = {
  variant: UIVariant
  partialPurseData: PartialPurseData
  educationDistributionRules: ProgramPurseResource['distributions_rules']['education_distributions']
  onBack?: () => void
  onSuccessfulSave: () => void
  onChangeRule?: () => void
  onNavigateToSpecialNeedsModifier: () => void
  extraModifierData?: ExtraModifierData
  onConfigureModifier?: (code: ModifierCode) => void
}

type FormValues = {
  education_rules: Record<string, number>
}

export default function EducationTypeRuleDetails({
  variant,
  partialPurseData,
  educationDistributionRules,
  // navigation buttons
  onBack, // configure → back to chooser
  onSuccessfulSave, // configure/view → save action
  onChangeRule, // view → back to chooser
  onNavigateToSpecialNeedsModifier,
  extraModifierData,
  onConfigureModifier,
}: Props) {
  const isView = variant === 'all_settings_view'
  const [confirmOpen, setConfirmOpen] = useState(false)
  const { toast } = useToast()

  const defaultValues: FormValues = {
    education_rules: educationDistributionRules.reduce(
      (acc, rule) => {
        acc[rule.education_type_id] = rule.base_amount || 0
        return acc
      },
      {} as Record<string, number>
    ),
  }

  const { control, handleSubmit, reset } = useForm<FormValues>({
    values: defaultValues,
  })

  const queryClient = useQueryClient()
  const updateEducationMutation = useUpdateEducationDistributions()
  const updatePurseMutation = useUpdateProgramPurse()
  const isSubmitting =
    updateEducationMutation.status === 'pending' ||
    updatePurseMutation.status === 'pending'

  const onSubmit = async (data: FormValues) => {
    const payload = Object.entries(data.education_rules).map(
      ([education_type_id, base_amount]) => ({
        education_type_id: Number(education_type_id),
        base_amount: base_amount,
      })
    )

    updatePurseMutation.mutate(
      {
        params: {
          purse_id: partialPurseData.id,
          program_id: partialPurseData.programId,
        },
        payload: { awarding_method_code: 'education_type' },
      },
      {
        onError: (error) => {
          toast({
            title: 'Error',
            description:
              error.message || 'Failed to update education distribution rules',
          })
        },
      }
    )

    updateEducationMutation.mutate(
      {
        params: {
          programId: partialPurseData.programId,
          purseId: partialPurseData.id,
        },
        payload,
      },
      {
        onSuccess: () => {
          reset(data)
          queryClient.invalidateQueries({
            queryKey: PURSE_KEYS.PURSE_DETAILS(
              partialPurseData.id.toString(),
              partialPurseData.programId
            ),
          })
          queryClient.invalidateQueries({
            queryKey: PURSE_KEYS.PURSES(partialPurseData.programId),
          })
          onSuccessfulSave()
          toast({
            title: 'Success',
            description: 'Education distribution rules updated successfully',
          })
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description:
              error.message || 'Failed to update education distribution rules',
          })
        },
      }
    )
  }

  return (
    <div className="w-full px-3 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl space-y-6">
        {isView && (
          <>
            <AutoDistributionCard partialPurseData={partialPurseData} />
            <DistributionRulesCard />
          </>
        )}

        {isView && (
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <School className="h-6 w-6" aria-hidden />
                  <div className="mt-2 text-sm font-medium text-gray-900">
                    Education Type
                  </div>
                  <p className="mt-1 text-xs text-gray-600">
                    Set different amounts based on homeschool, private school,
                    public school, etc
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmOpen(true)}
                >
                  Change Rule
                </Button>

                <ConfirmChangeRuleDialog
                  open={confirmOpen}
                  onOpenChange={setConfirmOpen}
                  onConfirm={onChangeRule ?? (() => {})}
                />
              </div>
            </CardHeader>
          </Card>
        )}

        <form
          id="education-distribution-rules"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Card>
            <CardHeader>
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                  Award Amounts by School Type
                </h3>
                {!isView && (
                  <p className="mt-1 text-sm text-gray-700">
                    Set scholarship amounts for each education type and grade
                    level.
                  </p>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4">
                {educationDistributionRules.map((rule) => (
                  <div key={rule.education_type_code} className="space-y-1.5">
                    <Label htmlFor={rule.education_type_code} weight="medium">
                      {rule.education_type_name}
                    </Label>
                    <div className="relative">
                      <span
                        aria-hidden
                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500"
                      >
                        $
                      </span>
                      <ControlledCurrencyInput
                        name={`education_rules.${rule.education_type_id}`}
                        control={control}
                        id={rule.education_type_code}
                        placeholder="0"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div
                className={cn(
                  'mt-6 flex gap-3',
                  isView ? 'justify-start' : 'justify-end'
                )}
              >
                {isView ? (
                  <SubmitButton
                    status={updateEducationMutation.status}
                    form="education-distribution-rules"
                    variant="primary"
                    size="sm"
                    className="w-auto"
                    buttonTxt="Save Changes"
                    submittingText="Saving..."
                  />
                ) : (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={onBack}
                      disabled={isSubmitting}
                    >
                      Back
                    </Button>
                    <SubmitButton
                      status={updateEducationMutation.status}
                      form="education-distribution-rules"
                      variant="primary"
                      size="sm"
                      className="w-auto"
                      buttonTxt="Continue"
                      submittingText="Saving..."
                    />
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </form>
        {isView && (
          <>
            <SpecialNeedsAwardCard
              onConfigure={onNavigateToSpecialNeedsModifier}
            />
            {extraModifierData && onConfigureModifier && (
              <ExtraModifierCards
                modifiers={extraModifierData}
                onConfigureModifier={onConfigureModifier}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}
